import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { api } from '../../lib/api.js'
import useSaveShortcut from '../../lib/useSaveShortcut.js'
import { toast } from '../../components/feedback/Toast.jsx'
import Segmented from '../../components/ui/Segmented.jsx'
import { SAMPLE_RATES, BIT_DEPTHS, REMOTE_METHODS } from './fields.js'

const ZOOM_CHOICES = ['Our link', 'Their link']

/**
 * Set up a client (Charles, 2026-09-26): the same things the Notion records
 * held, as choices instead of free text — sample rate, bit depth, how they
 * connect, whose Zoom link, their Riverside link — plus file delivery and Details, the note that
 * closes the setup box. New client when `initial` has no id; otherwise
 * edits the record, name included (the client page's Edit button). It never
 * touches the client's free page under the Updates (Charles, 2026-09-29).
 * Same sheet as the login form: full-screen on phones, a card on desktop.
 */
export default function ClientForm({ initial = {}, onClose }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const editing = Boolean(initial.id)
  const [name, setName] = useState(initial.name || '')
  const [sampleRate, setSampleRate] = useState(SAMPLE_RATES.includes(initial.sampleRate) ? initial.sampleRate : null)
  const [bitDepth, setBitDepth] = useState(initial.bitDepth || null)
  const [methods, setMethods] = useState(initial.remoteMethods || [])
  const [zoomOwner, setZoomOwner] = useState(initial.zoomLinkOwner || null)
  const [zoomLink, setZoomLink] = useState(initial.zoomLink || '')
  const [riversideLink, setRiversideLink] = useState(initial.riversideLink || '')
  const [remotePref, setRemotePref] = useState(initial.remotePref || '')
  const [fileDelivery, setFileDelivery] = useState(initial.fileDelivery || '')
  const [details, setDetails] = useState(initial.details || '')
  const [busy, setBusy] = useState(false)
  const formRef = useRef(null)
  // Notion kept a few odd sample rates as free text; say so rather than
  // quietly clearing one the pickers can't show.
  const oddRate = initial.sampleRate && !SAMPLE_RATES.includes(initial.sampleRate) ? initial.sampleRate : null

  useSaveShortcut(() => formRef.current?.requestSubmit())

  const usesZoom = methods.includes('Zoom')
  const usesRiverside = methods.includes('Riverside')
  const toggleMethod = (m) =>
    setMethods((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : REMOTE_METHODS.filter((x) => x === m || cur.includes(x))))

  async function submit(e) {
    e.preventDefault()
    if (busy || !name.trim()) return
    setBusy(true)
    const body = {
      ...(sampleRate || !oddRate ? { sampleRate } : {}),
      bitDepth,
      remoteMethods: methods,
      zoomLinkOwner: usesZoom ? zoomOwner : null,
      zoomLink: usesZoom && zoomOwner === 'theirs' ? zoomLink : null,
      riversideLink: usesRiverside ? riversideLink : null,
      remotePref,
      fileDelivery,
      details,
    }
    try {
      if (editing) {
        await api(`/clients/${initial.id}`, { method: 'PATCH', body: { ...body, name: name.trim(), pageVersion: initial.pageVersion } })
        toast('Client saved')
      } else {
        const res = await api('/clients', { body: { name: name.trim(), ...body } })
        qc.invalidateQueries({ queryKey: ['spaces'] })
        navigate(`/clients/${res.slug}`)
      }
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['page', 'clients'] })
      qc.invalidateQueries({ queryKey: ['space', 'clients'] })
      qc.invalidateQueries({ queryKey: ['search'] })
      onClose()
    } catch (err) {
      toast(err.message || 'Couldn’t save', 'error')
    } finally {
      setBusy(false)
    }
  }

  const field =
    'tap w-full rounded-xl border border-hairline bg-grouped px-4 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link'
  const label = 'section-header mb-1.5 block'

  // Portaled to <body>, like the login form: inside the shell's scroll pane
  // iOS treats fixed as absolute and the tab bar covers the Save button.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 md:items-center" onClick={onClose}>
      <div
        className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom))] md:max-w-lg md:rounded-2xl md:pb-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">{editing ? 'Edit client' : 'New client'}</h2>
          <button onClick={onClose} aria-label="Close" className="tap flex items-center justify-center rounded-lg text-muted active:bg-grouped">
            <X size={20} />
          </button>
        </div>

        <form ref={formRef} onSubmit={submit} className="space-y-5">
          <input required autoFocus={!editing} placeholder="Client name" value={name} onChange={(e) => setName(e.target.value)} className={field} />

          <div>
            <span className={label}>Sample rate</span>
            <Segmented options={SAMPLE_RATES} value={sampleRate} onChange={setSampleRate} />
            {oddRate && !sampleRate && <p className="mt-1.5 text-xs text-muted">Currently “{oddRate}” — pick one to replace it.</p>}
          </div>

          <div>
            <span className={label}>Bit depth</span>
            <Segmented options={BIT_DEPTHS} value={bitDepth} onChange={setBitDepth} />
          </div>

          <div>
            <span className={label}>Remote connection</span>
            <p className="-mt-0.5 mb-2 text-xs text-muted">Tick every one they use.</p>
            <div className="flex flex-wrap gap-2">
              {REMOTE_METHODS.map((m) => {
                const on = methods.includes(m)
                return (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleMethod(m)}
                    className={
                      'tap flex items-center gap-1.5 border px-3 py-1.5 text-sm font-medium transition-colors duration-200 ' +
                      (on ? 'border-navy bg-navy text-white' : 'border-hairline text-ink active:bg-grouped')
                    }
                    style={{ borderWidth: '1.5px' }}
                  >
                    {on && <Check size={14} strokeWidth={2.5} />}
                    {m}
                  </button>
                )
              })}
            </div>
          </div>

          {usesZoom && (
            <div>
              <span className={label}>Zoom link</span>
              <Segmented
                options={ZOOM_CHOICES}
                value={zoomOwner === 'ours' ? ZOOM_CHOICES[0] : zoomOwner === 'theirs' ? ZOOM_CHOICES[1] : null}
                onChange={(v) => setZoomOwner(v === ZOOM_CHOICES[0] ? 'ours' : v === ZOOM_CHOICES[1] ? 'theirs' : null)}
              />
              {zoomOwner === 'theirs' && (
                <input
                  placeholder="Their Zoom link"
                  value={zoomLink}
                  onChange={(e) => setZoomLink(e.target.value)}
                  inputMode="url"
                  autoComplete="off"
                  className={`${field} mt-2`}
                />
              )}
            </div>
          )}

          {/* Their Riverside studio link, the way Zoom has one (Charles,
              2026-09-30). Shown only while Riverside is ticked. */}
          {usesRiverside && (
            <div>
              <span className={label}>Riverside link</span>
              <input
                placeholder="Their Riverside studio link"
                value={riversideLink}
                onChange={(e) => setRiversideLink(e.target.value)}
                inputMode="url"
                autoComplete="off"
                className={field}
              />
            </div>
          )}

          {/* Notion's free-text remote notes: shown only where a record still
              has some, so nothing written there is lost. */}
          {initial.remotePref && (
            <div>
              <span className={label}>Remote notes</span>
              <textarea value={remotePref} onChange={(e) => setRemotePref(e.target.value)} rows={3} className={field} />
            </div>
          )}

          <div>
            <span className={label}>File delivery</span>
            <textarea
              placeholder="Where the files go, e.g. their Dropbox link or who to email"
              value={fileDelivery}
              onChange={(e) => setFileDelivery(e.target.value)}
              rows={2}
              className={field}
            />
          </div>

          <div>
            <span className={label}>Details</span>
            <textarea
              placeholder="Anything else about what they need: mics, how they like the session run, who to expect…"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={Math.min(14, Math.max(5, details.split('\n').length + 1))}
              className={field}
            />
          </div>

          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="tap w-full rounded-xl bg-navy py-3 font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Saving…' : editing ? 'Save' : 'Add client'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  )
}
